import { useHotelContext } from '@/pages/hotels/useHotelContext';
import { formatDate } from '@startupquickstart/frontend';
import { useStatCount } from '@startupquickstart/frontend';
import {
  EmptyTableState,
  Loading,
  MapPreview,
  RichTextContent,
  StatCardGrid,
  useApp,
} from '@startupquickstart/frontend';
import { Building2, ExternalLink, FileText, Globe, Mail, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';

export function HotelOverviewTab() {
  const { hotel, isLoading } = useHotelContext();
  const { config } = useApp();
  const id = hotel?.id;

  const { data: toursData } = useStatCount(
    `/hotels/${id}/tours`,
    ['Hotel', id!, 'tours-count'],
    !!id,
  );
  const { data: amenitiesData } = useStatCount(
    `/hotels/${id}/amenities`,
    ['Hotel', id!, 'amenities-count'],
    !!id,
  );
  const { data: offersData } = useStatCount(
    `/hotels/${id}/promotional-offers`,
    ['Hotel', id!, 'offers-count'],
    !!id,
  );
  const { data: guestsData } = useStatCount(
    `/hotels/${id}/scanners`,
    ['Hotel', id!, 'guests-count'],
    !!id,
  );

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loading />
      </div>
    );
  }

  if (!hotel) return null;

  const stats = [
    { label: 'Active Tours', value: toursData?.pagination?.total ?? '-' },
    { label: 'Amenities', value: amenitiesData?.pagination?.total ?? '-' },
    { label: 'Offers', value: offersData?.pagination?.total ?? '-' },
    { label: 'Total Guests', value: guestsData?.pagination?.total ?? '-' },
  ];

  const cityStateZip = [hotel.city, hotel.state, hotel.postalCode]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="space-y-6">
      <StatCardGrid stats={stats} />

      {/* Two-column content */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left column */}
        <div className="lg:col-span-3 space-y-6">
          {/* Contact Information card */}
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">
                Contact Information
              </h2>
              <p className="text-sm text-muted-foreground">How guests can reach the hotel</p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="min-w-0">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1.5">
                    <Phone className="h-3.5 w-3.5 shrink-0" />
                    Phone
                  </div>
                  <div className="text-sm">
                    {hotel.phoneNumber || <span className="text-muted-foreground">-</span>}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1.5">
                    <Mail className="h-3.5 w-3.5 shrink-0" />
                    Email
                  </div>
                  <div className="text-sm truncate">
                    {hotel.email ? (
                      <a
                        href={`mailto:${hotel.email}`}
                        className="text-primary hover:underline"
                        title={hotel.email}
                      >
                        {hotel.email}
                      </a>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1.5">
                    <Globe className="h-3.5 w-3.5 shrink-0" />
                    Website
                  </div>
                  <div className="text-sm truncate">
                    {hotel.website ? (
                      <a
                        href={
                          hotel.website.startsWith('http')
                            ? hotel.website
                            : `https://${hotel.website}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline inline-flex items-center gap-1"
                        title={hotel.website.replace(/^https?:\/\//, '')}
                      >
                        <span className="truncate">
                          {hotel.website.replace(/^https?:\/\//, '')}
                        </span>
                        <ExternalLink className="h-3 w-3 shrink-0" />
                      </a>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                </div>
                <div className="min-w-0">
                  <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5 shrink-0" />
                    Organization
                  </div>
                  <div className="text-sm truncate">
                    {hotel.organization ? (
                      <Link
                        to={`/organizations/${hotel.organization.id}`}
                        className="text-primary hover:underline"
                      >
                        {hotel.organization.name}
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* About card */}
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">About</h2>
              <p className="text-sm text-muted-foreground">Hotel description and details</p>
            </div>
            <div className="p-6">
              {hotel.description ? (
                <RichTextContent content={hotel.description} />
              ) : (
                <EmptyTableState
                  icon={<FileText className="h-6 w-6" />}
                  title="No description"
                  description="Edit to add a description"
                />
              )}
            </div>
            <div className="border-t px-6 py-4 text-sm text-muted-foreground flex flex-wrap gap-x-6 gap-y-1">
              <span>Created: {formatDate(hotel.createdAt)}</span>
              <span>Updated: {formatDate(hotel.updatedAt)}</span>
            </div>
          </div>
        </div>

        {/* Right column - Location */}
        <div className="lg:col-span-2 space-y-6">
          <div className="rounded-xl border bg-card shadow-sm">
            <div className="px-6 py-4 border-b">
              <h2 className="text-lg font-medium tracking-tight text-zinc-900">Location</h2>
              <p className="text-sm text-muted-foreground">Address and geographic details</p>
            </div>
            <div className="p-6 space-y-0">
              <MapPreview
                latitude={hotel.latitude}
                longitude={hotel.longitude}
                googleMapsApiKey={config?.googleMapsApiKey}
                alt={`Map of ${hotel.name}`}
                className="mb-4"
              />

              {/* Location details */}
              <div className="divide-y">
                {hotel.streetAddress && (
                  <div className="py-3 first:pt-0">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                      Street Address
                    </div>
                    <div className="text-sm">
                      {hotel.streetAddress}
                      {hotel.streetAddress_2 && `, ${hotel.streetAddress_2}`}
                    </div>
                  </div>
                )}
                {cityStateZip && (
                  <div className="py-3">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                      City, State, ZIP
                    </div>
                    <div className="text-sm">{cityStateZip}</div>
                  </div>
                )}
                {hotel.greaterArea && (
                  <div className="py-3">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                      Greater Area
                    </div>
                    <div className="text-sm">
                      <Link
                        to={`/greater-areas/${hotel.greaterArea.id}`}
                        className="text-primary hover:underline"
                      >
                        {hotel.greaterArea.name}
                      </Link>
                    </div>
                  </div>
                )}
                {hotel.neighborhood && (
                  <div className="py-3">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                      Neighborhood
                    </div>
                    <div className="text-sm">
                      <Link
                        to={`/neighborhoods/${hotel.neighborhood.id}`}
                        className="text-primary hover:underline"
                      >
                        {hotel.neighborhood.name}
                      </Link>
                    </div>
                  </div>
                )}
                {hotel.latitude != null && hotel.longitude != null && (
                  <div className="py-3">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                      Coordinates
                    </div>
                    <div className="text-sm">
                      {hotel.latitude}, {hotel.longitude}
                    </div>
                  </div>
                )}
                {hotel.timezone && (
                  <div className="py-3">
                    <div className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
                      Timezone
                    </div>
                    <div className="text-sm">{hotel.timezone}</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
